import {exists} from '@aboutyou/utils';
import React, {FC} from 'react';
import {Redirect as ReactRouterRedirect} from 'react-router';
import {RedirectProps} from 'react-router-dom';
import {useRouterContext} from './context';
import {useSearch} from './useLocation';

const ROUTER_REDIRECT_ID = Symbol('ROUTER_REDIRECT_ID');

type RedirectType = {
  __router_redirect_id__: typeof ROUTER_REDIRECT_ID;
  redirectTo: string;
  statusCode: number;
};

export const createRedirectHint = (
  redirectTo: string,
  statusCode: number,
): RedirectType => {
  return {
    __router_redirect_id__: ROUTER_REDIRECT_ID,
    redirectTo,
    statusCode,
  };
};

export const isRedirectHint = (
  error: unknown | RedirectType,
): error is RedirectType => {
  return (
    exists(error) &&
    Object.prototype.hasOwnProperty.call(error, '__router_redirect_id__') &&
    (error as RedirectType).__router_redirect_id__ === ROUTER_REDIRECT_ID
  );
};

interface AdditionalRedirectProps {
  statusCode?: 302 | 301;
  keepSearch?: boolean;
}

export const Redirect: FC<RedirectProps & AdditionalRedirectProps> = ({
  statusCode,
  to,
  keepSearch,
  ...props
}) => {
  const search = useSearch();
  const staticContext = useRouterContext();
  if (staticContext) {
    staticContext.statusCode = statusCode || 302;
  }

  if (keepSearch) {
    const toWithSearch =
      typeof to === 'string'
        ? {
            pathname: to,
            search,
          }
        : {...to, search};
    return <ReactRouterRedirect {...props} to={toWithSearch} />;
  }

  return <ReactRouterRedirect to={to} {...props} />;
};
